import { createFileRoute, Link, Navigate } from '@tanstack/react-router';

import { ClockTimeCorrectionCard } from '@apps/web/components/settings/clock_time_correction_card';
import { OutreachTemplatesCard } from '@apps/web/components/settings/outreach_templates_card';
import { PlanOfCareDutyMonitoringCard } from '@apps/web/components/settings/plan_of_care_duty_monitoring_card';
import {
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import { useOrganizationDataSourceAccounts } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/settings/clock-in-out')({
    component: ClockInOutPage,
});

function ClockInOutPage() {
    const {
        organizationId,
        gates,
        planOfCareDutyMonitoringAvailable,
        clockWriteEnabled,
    } = useSettingsGates();
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        { reason: 'settings-clock-in-out' },
    );
    // Corrections only have a verified punch-edit protocol for these two
    // EHRs, and need at least one clock-write flag; the backend fails closed
    // on both, so the toggle would otherwise control nothing.
    const clockTimeCorrectionAvailable =
        clockWriteEnabled &&
        (dataSourceAccounts?.some(
            (account) =>
                account.type === 'wellsky' || account.type === 'axiscare',
        ) ??
            false);
    if (!gates.canManageOrgSettings) {
        return <Navigate to="/settings" replace={true} />;
    }
    if (organizationId === null) {
        return null;
    }

    return (
        <SettingsPageShell
            title="Clock in / Clock out"
            description={
                clockTimeCorrectionAvailable
                    ? 'Control caregiver clock-time corrections and clock reminder outreach.'
                    : 'Control clock reminder outreach settings.'
            }
        >
            {clockTimeCorrectionAvailable ? (
                <ClockTimeCorrectionCard organizationId={organizationId} />
            ) : null}
            <OutreachTemplatesCard
                organizationId={organizationId}
                outreachTypes={
                    planOfCareDutyMonitoringAvailable
                        ? ['clock_in', 'clock_out', 'plan_of_care_duties']
                        : ['clock_in', 'clock_out']
                }
                outreachTypeSettings={
                    planOfCareDutyMonitoringAvailable
                        ? {
                              plan_of_care_duties: (
                                  <PlanOfCareDutyMonitoringCard
                                      organizationId={organizationId}
                                  />
                              ),
                          }
                        : undefined
                }
                title="Clock reminder templates"
                description="Configure SMS templates and delays for clock-in and clock-out reminders."
                variant="embedded"
                headerAction={
                    <Link
                        to="/settings/skip-allow-lists"
                        className="text-foreground text-sm font-medium whitespace-nowrap hover:underline pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
                    >
                        Manage skip lists →
                    </Link>
                }
            />
        </SettingsPageShell>
    );
}
